import { useEffect, useMemo, useState } from 'react'

import { Box, Container, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { isBefore, isAfter, startOfDay } from 'date-fns'

import { ErrorBoundary } from '@scoir/error-reporter'
import { PageTitle } from '@scoir/headers'

import useCollegeFeatures from 'common/use-college-features'
import features from 'common/college-features'
import { useScid } from 'store/user/hooks'
import { useAppCycle } from 'store/application-cycle/hooks'
import useTitle from 'common/use-title'
import api from 'common/api'

import CardMetric from './components/CardMetric'
import ApplicantChart from './ApplicantChart'
import ApplicantsTable from './ApplicantsTable'
import FocusedApplicantDrawer from './FocusedApplicantDrawer'
import ScoirApplyButtonToggle from './ScoirApplyButtonToggle'
import Banner from './components/Banner'

const useStyles = makeStyles((theme) => ({
  pageHeader: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  pageSubheader: {
    fontSize: theme.typography.pxToRem(16),
  },
  metricContainer: {
    '& > *': {
      marginBottom: theme.spacing(3),
    },
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      justifyContent: 'space-between',
      gridGap: theme.spacing(3),
      marginTop: theme.spacing(3),
      flexWrap: 'wrap',
      '& > *': {
        flexGrow: 1,
        flexBasis: '30%',
      },
    },
  },
}))

type ApplicantData = {
  month: number
  year: number
  notInitiated: number
  submitted: number
  initiated: number
}

type ApplicantMetrics = {
  notInitiated: number
  initiated: number
  submitted: number
}

type countResponse = {
  counts: ApplicantMetrics
  months: ApplicantData[]
}

const calculateShowBanner = () => {
  const today = new Date()
  const rollover = startOfDay(new Date().setMonth(6, 1))
  const newAppCycleStarts = startOfDay(new Date().setMonth(7, 1))
  return isAfter(today, rollover) && isBefore(today, newAppCycleStarts)
}

const ApplicantsRoute = () => {
  useTitle('Scoir - Applicants')
  const { metricContainer, pageHeader, pageSubheader } = useStyles()
  const [data, setData] = useState<ApplicantData[]>()
  const [counts, setCounts] = useState<ApplicantMetrics>()
  const [hasError, setHasError] = useState(false)
  const isApplicationIntegrationEnabled = useCollegeFeatures(features.APPLICATION_INTEGRATION)
  const scid = useScid()
  const [hasApplyFocus, setHasApplyFocus] = useState(false)
  const appCycle = useAppCycle()
  useEffect(() => {
    if (isApplicationIntegrationEnabled) {
      api
        .get<countResponse>(`/api/colleges/${scid}/applicants/metrics`)
        .then((response) => {
          setData(response.months)
          setCounts(response.counts)
        })
        .catch(() => {
          setHasError(true)
        })
    }
  }, [isApplicationIntegrationEnabled, scid])
  const showBanner = useMemo(() => calculateShowBanner(), [])
  return (
    <Box mb={5}>
      {showBanner && <Banner appCycle={appCycle} />}
      <FocusedApplicantDrawer />
      {isApplicationIntegrationEnabled && (
        <ErrorBoundary>
          <Container maxWidth="lg">
            <PageTitle title="Applicants" />
            <Box mt={4}>
              <Typography
                variant="h6"
                component="h2"
                className={pageHeader}
              >
                {appCycle - 1}-{appCycle} Applicant Insights
              </Typography>
              <Typography
                variant="body2"
                className={pageSubheader}
              >
                Understand your application volume for Apply Coalition with Scoir
              </Typography>
            </Box>
          </Container>
          <Container maxWidth="lg">
            <Box
              className={metricContainer}
              mt={4}
            >
              <CardMetric
                title="Method Selected"
                subTitle="Applicants who have selected Scoir as their app method"
                metric={counts?.notInitiated}
              />
              <CardMetric
                title="In-Progress"
                subTitle="Applicants with completed Application Profile in Scoir, but incomplete supplement"
                metric={counts?.initiated}
              />
              <CardMetric
                title="Submitted"
                subTitle="Applicants with completed Application Profile in Scoir and completed supplement"
                metric={counts?.submitted}
              />
            </Box>
            {<ApplicantChart data={data} />}
            {hasError && (
              <Box
                mt={1}
                textAlign="center"
              >
                <Typography
                  color="error"
                  role="alert"
                >
                  Unable to load applicant metrics
                </Typography>
              </Box>
            )}
          </Container>
        </ErrorBoundary>
      )}
      <ErrorBoundary>
        <Container maxWidth="lg">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            mt={4}
          >
            <Box
              mr={1}
              minWidth={250}
            >
              <Typography
                variant="h6"
                component="h2"
                className={pageHeader}
              >
                {appCycle - 1}-{appCycle} Applicant Management
              </Typography>
              <Typography
                variant="body2"
                className={pageSubheader}
              >
                View and manage applicant details and documents
              </Typography>
            </Box>
            {isApplicationIntegrationEnabled && (
              <Box
                minWidth={250}
                my={1}
              >
                <ScoirApplyButtonToggle
                  hasApplyFocus={hasApplyFocus}
                  onChangeApplyFocus={(isFocused) => setHasApplyFocus(isFocused)}
                />
              </Box>
            )}
          </Box>
          <Box mt={4}>
            <ApplicantsTable scoirApplyOnly={hasApplyFocus} />
          </Box>
        </Container>
      </ErrorBoundary>
    </Box>
  )
}

export default ApplicantsRoute
